"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TimeInput = void 0;
const react_1 = __importDefault(require("react"));
const util_1 = require("@app/util");
const classnames_1 = __importDefault(require("classnames"));
const __1 = require("../..");
require("./index.scss");
const parseValue = (value, maxDigits) => {
    const stringValue = value.toString();
    const zeroes = '0'.repeat(Math.max(0, maxDigits - stringValue.length));
    const targetValue = Number.isNaN(value) ? '' : `${zeroes}${stringValue}`;
    return targetValue;
};
const processSafeValue = (value, min, max) => {
    const _value = Number.isNaN(value) ? 0 : value;
    const safeValue = Math.min(Math.max(_value, min), max);
    return safeValue;
};
const handleValueChange = (processValue) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const value = parseInt(e.target.value);
    const min = parseInt(e.target.min);
    const max = parseInt(e.target.max);
    const safeValue = processSafeValue(value, min, max);
    processValue(safeValue);
};
const handleKeyDown = (target, processDiff) => (e) => {
    if ((0, util_1.isNull)(target))
        return;
    const min = parseInt(target.min);
    const max = parseInt(target.max);
    const value = parseInt(target.value);
    const diff = e.key === 'ArrowUp' ? +1 : e.key === 'ArrowDown' ? -1 : 0;
    const safeValue = processSafeValue(value + diff, min, max);
    processDiff(safeValue);
};
const TimeInput = ({ onChange, value, disabled, name, maxHours = 23, disablePicker = false, label, error, }) => {
    const hoursInputRef = react_1.default.useRef(null);
    const minutesInputRef = react_1.default.useRef(null);
    const hoursDropdownRef = react_1.default.useRef(null);
    const minutesDropdownRef = react_1.default.useRef(null);
    const [innerTime, setInnerTime] = react_1.default.useState({
        minutes: 0,
        hours: 0,
    });
    const maxHourDigits = maxHours.toString().length;
    const setValue = react_1.default.useCallback((value, key) => {
        const newInnerTime = { ...innerTime, [key]: value };
        setInnerTime(newInnerTime);
        onChange(newInnerTime.hours * 60 + newInnerTime.minutes);
    }, [onChange, setInnerTime, innerTime]);
    const floatable = (0, util_1.useFloatable)();
    react_1.default.useEffect(() => {
        if ((0, util_1.isUndefined)(value) || (0, util_1.isNull)(value)) {
            onChange(0);
        }
        const _value = value ?? 0;
        const hours = Math.floor(_value / 60);
        const minutes = _value % 60;
        setInnerTime({
            hours,
            minutes,
        });
    }, [value]);
    react_1.default.useEffect(() => {
        if (!floatable.open)
            return;
        hoursDropdownRef.current?.scrollTo(0, 32 * innerTime.hours);
        minutesDropdownRef.current?.scrollTo(0, 32 * innerTime.minutes);
    }, [floatable.open]);
    react_1.default.useEffect(() => {
        const onfocushours = () => {
            hoursInputRef.current?.setSelectionRange(null, hoursInputRef.current.value.length);
        };
        const onfocusminutes = () => {
            minutesInputRef.current?.setSelectionRange(null, minutesInputRef.current.value.length);
        };
        hoursInputRef.current?.addEventListener('focus', onfocushours);
        minutesInputRef.current?.addEventListener('focus', onfocusminutes);
        return () => {
            hoursInputRef.current?.removeEventListener('focus', onfocushours);
            minutesInputRef.current?.removeEventListener('focus', onfocusminutes);
        };
    }, [hoursInputRef.current, minutesInputRef.current]);
    const handleHoursChange = handleValueChange((inputVal) => {
        setValue(inputVal, 'hours');
    });
    const handleMinutesChange = handleValueChange((inputVal) => {
        setValue(inputVal, 'minutes');
    });
    const handleHoursKeyDown = handleKeyDown(hoursInputRef.current, (inputVal) => {
        setValue(inputVal, 'hours');
    });
    const handleMinutesKeyDown = handleKeyDown(minutesInputRef.current, (inputVal) => {
        setValue(inputVal, 'minutes');
    });
    const shouldShowError = (0, util_1.isDefined)(error) && (0, util_1.isNotNull)(error);
    return (react_1.default.createElement("div", { className: (0, classnames_1.default)('ds-time-input', { error: shouldShowError }) },
        (0, util_1.isDefined)(label) && (0, util_1.isNotNull)(label) && (react_1.default.createElement("div", { className: 'input-label' }, label)),
        react_1.default.createElement("div", { ...floatable.floatAnchor, onClick: (e) => {
                e.preventDefault();
                e.stopPropagation();
            }, className: (0, classnames_1.default)('ds-time-input-inner', { disabled }), "aria-label": name },
            !disablePicker ? (react_1.default.createElement(__1.Icon, { disabled: disabled, onClick: () => {
                    floatable.setOpen(true);
                }, ico: __1.Icon.ICONS.clock, color: 'primary', size: 'small' })) : null,
            react_1.default.createElement("input", { ref: hoursInputRef, value: parseValue(innerTime.hours, maxHourDigits), type: 'text', min: '0', max: maxHours, maxLength: maxHourDigits + 1, disabled: disabled, placeholder: 'HH', onChange: handleHoursChange, onKeyDown: handleHoursKeyDown, onFocus: () => {
                    floatable.setOpen(false);
                }, style: { width: `calc(${0.5 + 0.5 * maxHourDigits}em + 1px)` } }),
            react_1.default.createElement("span", null, ":"),
            react_1.default.createElement("input", { ref: minutesInputRef, value: parseValue(innerTime.minutes, 2), type: 'text', min: '0', max: '59', maxLength: 3, disabled: disabled, placeholder: 'MM', onChange: handleMinutesChange, onKeyDown: handleMinutesKeyDown, onFocus: () => {
                    floatable.setOpen(false);
                } })),
        (0, util_1.isDefined)(error) && (0, util_1.isNotNull)(error) && (react_1.default.createElement("span", { className: 'error-label' }, error)),
        (0, util_1.isDefined)(error) && (0, util_1.isNotNull)(error) && react_1.default.createElement(__1.Err, { message: error }),
        floatable.open && (react_1.default.createElement("div", { className: 'ds-time-selector', ...floatable.floatContainer },
            react_1.default.createElement("div", { ref: hoursDropdownRef },
                react_1.default.createElement("div", null, Array.from({ length: maxHours }).map((_, index) => (react_1.default.createElement("div", { onClick: () => {
                        setValue(index, 'hours');
                    }, className: (0, classnames_1.default)('ds-time-selector-item', {
                        selected: innerTime.hours === index,
                    }), key: `ds-time-selector-hours-${index}` }, index.toString().padStart(2, '0')))))),
            react_1.default.createElement("div", { ref: minutesDropdownRef },
                react_1.default.createElement("div", null, Array.from({ length: 60 }).map((_, index) => (react_1.default.createElement("div", { onClick: () => {
                        setValue(index, 'minutes');
                    }, className: (0, classnames_1.default)('ds-time-selector-item', {
                        selected: innerTime.minutes === index,
                    }), key: `ds-time-selector-minutes-${index}` }, index.toString().padStart(2, '0'))))))))));
};
exports.TimeInput = TimeInput;
